/*
 * MyLoanOfficer.ai — B2B SaaS marketing site
 *
 * Homepage for the platform itself, targeting mortgage brokerages and
 * real-estate companies as customers. Each customer becomes a "tenant"
 * with their own branded public site + backend portal.
 */
import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { applyBranding } from './branding';
import { getToken } from './portal/api';
import {
  Calculator, FileCheck, Users, Zap, Shield,
  ArrowRight, Check, Building2, Home as HomeIcon,
  ChevronRight, PlayCircle, Bot,
} from 'lucide-react';

// ============================================================================
// Palette — MyLoanOfficer.ai brand (distinct from any tenant)
// Deep indigo + electric lime — signals "modern AI infrastructure"
// ============================================================================
// Public contact address for the marketing page. Overridable per deployment
// (ticket 41); the literal stays as the fallback by Damien's decision 2026-09-03.
// Ticket 79: no literal address. Unset at build time → the footer hides the
// email and the demo CTA sends people to sign-up instead of a mailto.
const CONTACT_EMAIL = (process.env.REACT_APP_CONTACT_EMAIL || '').trim();
const DEMO_MAILTO = CONTACT_EMAIL ? `mailto:${CONTACT_EMAIL}?subject=Demo%20Request` : '/portal/signup';

const C = {
  bg:      '#0A0E27',           // deep near-black indigo
  bgAlt:   '#111637',
  fg:      '#E8ECFF',
  fgMuted: '#8B93C8',
  accent:  '#B4FF39',           // electric lime
  accentDeep: '#7BC81A',
  panel:   '#141A3A',
  border:  '#1F2649',
};

// ============================================================================
// Reusable pieces
// ============================================================================
function Nav() {
  return (
    <nav className="sticky top-0 z-40 backdrop-blur border-b" style={{ background: `${C.bg}dd`, borderColor: C.border }}>
      <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2" data-testid="mlo-brand">
          <img src="/brand/brokerforyou-white.png?v=3" alt="BrokerForYou" className="h-9 w-auto" />
        </Link>
        <div className="hidden md:flex items-center gap-8">
          <a href="#features" className="text-sm hover:text-white transition" style={{ color: C.fgMuted }}>Features</a>
          <a href="#audience" className="text-sm hover:text-white transition" style={{ color: C.fgMuted }}>Who it's for</a>
          <a href="#pricing" className="text-sm hover:text-white transition" style={{ color: C.fgMuted }}>Pricing</a>
          <a href="#faq" className="text-sm hover:text-white transition" style={{ color: C.fgMuted }}>FAQ</a>
        </div>
        <div className="flex items-center gap-3">
          {getToken() ? (
            <Link to="/portal" className="text-sm hover:text-white transition" style={{ color: C.fgMuted }} data-testid="mlo-signin">Open Portal</Link>
          ) : (
            <Link to="/portal/login" className="text-sm hover:text-white transition" style={{ color: C.fgMuted }} data-testid="mlo-signin">Sign in</Link>
          )}
          <a href="#pricing" className="px-4 py-2 rounded-lg font-semibold text-sm transition hover:shadow-lg"
             style={{ background: C.accent, color: C.bg }} data-testid="mlo-cta-header">
            Book a demo
          </a>
        </div>
      </div>
    </nav>
  );
}

function Hero() {
  return (
    <section className="relative overflow-hidden pt-16 pb-24 px-6">
      {/* Glow */}
      <div className="absolute top-20 left-1/2 -translate-x-1/2 w-[600px] h-[600px] rounded-full opacity-20 pointer-events-none"
           style={{ background: `radial-gradient(circle, ${C.accent}, transparent 60%)` }} />
      <div className="relative max-w-6xl mx-auto text-center">
        <h1 className="font-bold tracking-tight leading-[1.05]"
            style={{ fontSize: 'clamp(2.5rem, 6vw, 5.5rem)', color: C.fg }}>
          The AI-native platform<br />for <span style={{ color: C.accent }}>mortgage &amp; real estate</span> brokerages.
        </h1>
        <p className="mt-6 max-w-2xl mx-auto text-lg leading-relaxed" style={{ color: C.fgMuted }}>
          Give your loan officers and agents a modern portal — with an AI income calculator, guideline-cited assistant,
          e-sign, CRM, and a fully-branded client site.
        </p>
        <div className="mt-10 flex flex-col sm:flex-row gap-3 justify-center">
          <a href="#pricing" className="inline-flex items-center gap-2 px-6 py-3.5 rounded-lg font-bold transition hover:shadow-xl"
             style={{ background: C.accent, color: C.bg }} data-testid="hero-cta-primary">
            Book a 20-min demo <ArrowRight size={16} />
          </a>
          <a href="#features" className="inline-flex items-center gap-2 px-6 py-3.5 rounded-lg font-semibold transition hover:bg-white/5 border"
             style={{ color: C.fg, borderColor: C.border }} data-testid="hero-cta-secondary">
            <PlayCircle size={16} /> Tour the product
          </a>
        </div>
      </div>
    </section>
  );
}

function Features() {
  const features = [
    { icon: Calculator, title: 'AI Income Calculator', body: 'Drop tax returns → instant qualifying income across all 5 major agencies (Fannie, Freddie, FHA, VA, USDA) with citation-backed math.' },
    { icon: FileCheck, title: 'Full LOS + Compliance', body: 'Applications, disclosures, e-sign, and closing coordination — with audit trails auditors love.' },
    { icon: Bot, title: 'Audrey — Guideline-Cited AI', body: 'The only mortgage AI that quotes Fannie B3-3.2 and FHA 4000.1 by section number and links to the source.' },
    { icon: Users, title: 'Realtor + Borrower Portals', body: 'Every client gets a branded portal with real-time loan status, doc uploads, and e-sign.' },
    { icon: Zap, title: 'Weekly Guideline Refresh', body: 'Our system auto-checks Fannie/Freddie/FHA/VA/USDA bulletins weekly. You get notified before your competitors.' },
    { icon: Shield, title: 'Enterprise Security', body: 'Role-based access. Tenant isolation. Documents and SSNs encrypted at rest. Audit log on every action.' },
  ];
  return (
    <section id="features" className="py-24 px-6" style={{ background: C.bgAlt }}>
      <div className="max-w-6xl mx-auto">
        <div className="text-center mb-16">
          <div className="text-xs uppercase tracking-widest mb-3" style={{ color: C.accent }}>Everything your team needs</div>
          <h2 className="font-bold tracking-tight" style={{ fontSize: 'clamp(1.75rem, 4vw, 3rem)', color: C.fg }}>
            Built for how modern brokerages actually work.
          </h2>
        </div>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {features.map((f) => (
            <div key={f.title} className="p-6 rounded-2xl transition hover:scale-[1.02]"
                 style={{ background: C.panel, border: `1px solid ${C.border}` }}>
              <div className="w-10 h-10 rounded-lg flex items-center justify-center mb-4"
                   style={{ background: `${C.accent}22`, color: C.accent }}>
                <f.icon size={18} />
              </div>
              <div className="font-bold mb-2" style={{ color: C.fg }}>{f.title}</div>
              <div className="text-sm leading-relaxed" style={{ color: C.fgMuted }}>{f.body}</div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function Audience() {
  const rows = [
    {
      icon: Building2,
      title: 'For Mortgage Brokerages',
      color: '#5EEAD4',
      bullets: [
        'Full Loan Origination System (URLA, disclosures, e-sign, closing)',
        'AI Income Calculator + Form 1084/91/92 worksheets',
        'Compliance module (TRID, HMDA, MCR)',
        'Branded borrower + realtor portals',
      ],
    },
    {
      icon: HomeIcon,
      title: 'For Real Estate Brokerages',
      color: '#F0ABFC',
      bullets: [
        'Full Realtor CRM (leads, deals, tasks)',
        'Deal / transaction management from listing to close',
        'AI assistant trained on real-estate guidance',
        'Branded agent sites + client portals',
      ],
    },
  ];
  return (
    <section id="audience" className="py-24 px-6">
      <div className="max-w-6xl mx-auto">
        <div className="text-center mb-16">
          <div className="text-xs uppercase tracking-widest mb-3" style={{ color: C.accent }}>Two products, one platform</div>
          <h2 className="font-bold tracking-tight" style={{ fontSize: 'clamp(1.75rem, 4vw, 3rem)', color: C.fg }}>
            Whether you close loans or list homes.
          </h2>
        </div>
        <div className="grid md:grid-cols-2 gap-6">
          {rows.map((r) => (
            <div key={r.title} className="p-8 rounded-2xl" style={{ background: C.panel, border: `1px solid ${C.border}` }}>
              <div className="w-12 h-12 rounded-xl flex items-center justify-center mb-5"
                   style={{ background: `${r.color}22`, color: r.color }}>
                <r.icon size={22} />
              </div>
              <div className="font-bold text-xl mb-4" style={{ color: C.fg }}>{r.title}</div>
              <ul className="space-y-3">
                {r.bullets.map((b) => (
                  <li key={b} className="flex items-start gap-2 text-sm" style={{ color: C.fgMuted }}>
                    <Check size={14} style={{ color: r.color, flexShrink: 0, marginTop: 3 }} />
                    <span>{b}</span>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function Pricing() {
  const tiers = [
    { name: 'Starter', price: '$99', period: '/user/mo', tagline: 'Solo LO or small team', highlight: false,
      bullets: ['AI Income Calc', 'Basic CRM', 'Branded client portal', 'Email support', 'Up to 3 users'] },
    { name: 'Pro', price: '$299', period: '/user/mo', tagline: 'Growing brokerages', highlight: true,
      bullets: ['Everything in Starter', 'Full LOS + Compliance', 'Audrey Guideline AI', 'Weekly guideline refresh', 'Custom branding', 'Up to 25 users', 'Priority support'] },
    { name: 'Enterprise', price: 'Custom', period: '', tagline: '50+ agents, custom integrations', highlight: false,
      bullets: ['Everything in Pro', 'Unlimited users', 'SSO / SAML', 'Dedicated success manager', 'Custom domain', 'API access'] },
  ];
  return (
    <section id="pricing" className="py-24 px-6" style={{ background: C.bgAlt }}>
      <div className="max-w-6xl mx-auto">
        <div className="text-center mb-16">
          <div className="text-xs uppercase tracking-widest mb-3" style={{ color: C.accent }}>Simple, transparent pricing</div>
          <h2 className="font-bold tracking-tight" style={{ fontSize: 'clamp(1.75rem, 4vw, 3rem)', color: C.fg }}>
            Pay for what you use. Cancel anytime.
          </h2>
        </div>
        <div className="grid md:grid-cols-3 gap-4 max-w-5xl mx-auto">
          {tiers.map((t) => (
            <div key={t.name} className="p-8 rounded-2xl relative transition hover:scale-[1.02]"
                 style={{
                   background: t.highlight ? `linear-gradient(180deg, ${C.accent}18, ${C.panel})` : C.panel,
                   border: `1px solid ${t.highlight ? C.accent : C.border}`,
                 }}
                 data-testid={`pricing-${t.name.toLowerCase()}`}>
              {t.highlight && (
                <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 rounded-full text-[10px] font-bold uppercase tracking-widest"
                     style={{ background: C.accent, color: C.bg }}>Most Popular</div>
              )}
              <div className="text-xs uppercase tracking-wider mb-2" style={{ color: C.fgMuted }}>{t.name}</div>
              <div className="flex items-baseline gap-1 mb-1">
                <span className="text-4xl font-bold" style={{ color: C.fg }}>{t.price}</span>
                <span className="text-sm" style={{ color: C.fgMuted }}>{t.period}</span>
              </div>
              <div className="text-sm mb-6" style={{ color: C.fgMuted }}>{t.tagline}</div>
              <a href={DEMO_MAILTO}
                 className="block w-full py-2.5 rounded-lg font-semibold text-center transition mb-6"
                 style={t.highlight
                   ? { background: C.accent, color: C.bg }
                   : { background: 'transparent', color: C.fg, border: `1px solid ${C.border}` }}
                 data-testid={`pricing-cta-${t.name.toLowerCase()}`}>
                Book a demo
              </a>
              <ul className="space-y-2.5">
                {t.bullets.map((b) => (
                  <li key={b} className="flex items-start gap-2 text-sm" style={{ color: C.fg }}>
                    <Check size={14} style={{ color: C.accent, flexShrink: 0, marginTop: 3 }} />
                    <span>{b}</span>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function FAQ() {
  const faqs = [
    { q: 'How long does onboarding take?', a: 'Typical onboarding is 3-5 business days. We migrate your existing pipeline, brand your site, and train your team on the AI tools.' },
    { q: 'Can I use my own domain?', a: 'Yes on the Pro and Enterprise plans. Point your DNS to us via CNAME and your branded portal lives at your domain.' },
    { q: 'What about compliance & data security?', a: 'Role-based access. Every tenant is fully isolated. Documents and SSNs are encrypted at rest, with an audit log on every action.' },
    { q: 'Do you integrate with LOS/DU/LPA/DocuSign?', a: 'DocuSign is native. DU/LPA and Optimal Blue integrations are on the Enterprise tier. Contact us for LOS import.' },
    { q: 'Real estate — do you support MLS/IDX?', a: 'MLS integrations are region-specific. We support HAR, Bright MLS, ARMLS out of the box; other MLSs by request.' },
  ];
  return (
    <section id="faq" className="py-24 px-6" style={{ background: C.bgAlt }}>
      <div className="max-w-3xl mx-auto">
        <div className="text-center mb-12">
          <div className="text-xs uppercase tracking-widest mb-3" style={{ color: C.accent }}>Questions</div>
          <h2 className="font-bold tracking-tight" style={{ fontSize: 'clamp(1.75rem, 4vw, 2.5rem)', color: C.fg }}>
            Frequently asked.
          </h2>
        </div>
        <div className="space-y-3">
          {faqs.map((f, i) => (
            <details key={i} className="p-5 rounded-xl cursor-pointer group" style={{ background: C.panel, border: `1px solid ${C.border}` }}>
              <summary className="flex items-center justify-between font-semibold list-none" style={{ color: C.fg }}>
                {f.q}
                <ChevronRight size={16} className="group-open:rotate-90 transition-transform" style={{ color: C.fgMuted }} />
              </summary>
              <p className="mt-3 text-sm leading-relaxed" style={{ color: C.fgMuted }}>{f.a}</p>
            </details>
          ))}
        </div>
      </div>
    </section>
  );
}

function CTA() {
  return (
    <section className="py-24 px-6">
      <div className="max-w-4xl mx-auto text-center p-12 rounded-3xl relative overflow-hidden"
           style={{ background: `linear-gradient(135deg, ${C.accent}22, ${C.panel})`, border: `1px solid ${C.accent}44` }}>
        <h2 className="font-bold" style={{ fontSize: 'clamp(1.75rem, 4vw, 3rem)', color: C.fg }}>
          Ready to modernize your brokerage?
        </h2>
        <p className="mt-4 text-lg" style={{ color: C.fgMuted }}>
          20-minute demo. See exactly how it fits your workflow.
        </p>
        <a href={DEMO_MAILTO}
           className="mt-8 inline-flex items-center gap-2 px-6 py-3.5 rounded-lg font-bold transition hover:shadow-xl"
           style={{ background: C.accent, color: C.bg }} data-testid="cta-book-demo">
          Book a demo <ArrowRight size={16} />
        </a>
      </div>
    </section>
  );
}

export function Footer() {
  return (
    <footer className="py-12 px-6 border-t" style={{ background: C.bg, borderColor: C.border }}>
      <div className="max-w-6xl mx-auto grid md:grid-cols-4 gap-8">
        <div className="md:col-span-2">
          <div className="flex items-center gap-2 mb-3">
            <img src="/brand/brokerforyou-white.png?v=3" alt="BrokerForYou" className="h-8 w-auto" />
          </div>
          <p className="text-sm max-w-md" style={{ color: C.fgMuted }}>
            The AI-native platform for mortgage &amp; real estate brokerages.
          </p>
        </div>
        <div>
          <div className="text-xs uppercase tracking-widest mb-3" style={{ color: C.fgMuted }}>Product</div>
          <div className="space-y-2 text-sm">
            <a href="#features" className="block hover:text-white" style={{ color: C.fgMuted }}>Features</a>
            <a href="#pricing" className="block hover:text-white" style={{ color: C.fgMuted }}>Pricing</a>
            {getToken() ? (
              <Link to="/portal" className="block hover:text-white" style={{ color: C.fgMuted }}>Open Portal</Link>
            ) : (
              <Link to="/portal/login" className="block hover:text-white" style={{ color: C.fgMuted }}>Sign in</Link>
            )}
          </div>
        </div>
        <div>
          <div className="text-xs uppercase tracking-widest mb-3" style={{ color: C.fgMuted }}>Contact</div>
          <div className="space-y-2 text-sm">
            {CONTACT_EMAIL && <a href={`mailto:${CONTACT_EMAIL}`} className="block hover:text-white" style={{ color: C.fgMuted }} data-testid="footer-contact-email">{CONTACT_EMAIL}</a>}
          </div>
        </div>
      </div>
      <div className="max-w-6xl mx-auto mt-8 pt-6 border-t text-xs text-center space-y-1" style={{ borderColor: C.border, color: C.fgMuted }}>
        <div>© {new Date().getFullYear()} Mambo Labs LLC · hello@mambolabs.io</div>
        <div>BrokerForYou is a product of Mambo Labs LLC.{CONTACT_EMAIL && <> Support: {CONTACT_EMAIL}</>}</div>
      </div>
    </footer>
  );
}


export default function MyLoanOfficerHome() {
  useEffect(() => { applyBranding({ title: 'BrokerForYou — AI platform for mortgage & real estate brokerages' }); }, []);
  return (
    <div className="min-h-screen" style={{ background: C.bg, color: C.fg }}>
      <Nav />
      <Hero />
      <Features />
      <Audience />
      <Pricing />
      <FAQ />
      <CTA />
      <Footer />
    </div>
  );
}
